import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso, lang) => {
  try {
    return new Date(iso).toLocaleDateString(lang === 'sv' ? 'sv-SE' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
};

export const BlogPost = () => {
  const { t, i18n } = useTranslation();
  const { lang, slug } = useParams();
  const activeLang = lang || i18n.language || 'en';
  const [post, setPost] = useState(undefined);

  useEffect(() => {
    fetch(`${API}/blog/${slug}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setPost(data))
      .catch(() => setPost(null));
  }, [slug]);

  if (post === undefined) {
    return <div className="min-h-screen bg-page px-5 md:px-8 py-16"><p className="serif-italic text-ink/70">Loading…</p></div>;
  }
  if (!post) return <Navigate to={`/${activeLang}/blog`} replace />;

  return (
    <article className="relative border-b border-ink pt-14 md:pt-20 pb-24 md:pb-32" data-testid={`blog-post-${slug}`}>
      <div className="max-w-[1400px] mx-auto px-5 md:px-8">
        {/* Back link + eyebrow */}
        <div className="flex items-center gap-3 mb-8">
          <Link
            to={`/${activeLang}/blog`}
            className="font-mono uppercase text-xs tracking-widest2 bg-ink text-page px-2 py-1 hover:bg-clay transition-colors"
            data-testid="blog-back"
          >
            ← {t('blog.backToList')}
          </Link>
          <span className="font-mono uppercase text-xs tracking-widest2 text-ink/70">
            {t('blog.eyebrow')}
          </span>
        </div>

        {/* Meta line above title */}
        <div className="font-mono uppercase text-[10px] md:text-xs tracking-widest2 text-ink/70 flex items-center gap-4 mb-5">
          <span>{formatDate(post.date, activeLang)}</span>
          <span aria-hidden>—</span>
          <span>{(post.tags || []).join(' · ')}</span>
        </div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="display text-5xl md:text-7xl lg:text-8xl leading-[0.9] text-ink max-w-5xl mb-10 md:mb-14"
        >
          {post.title}
        </motion.h1>

        {/* Cover image */}
        {post.cover && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mb-12 md:mb-16 box-line overflow-hidden"
          >
            <img
              src={post.cover}
              alt={post.cover_alt || post.title}
              className="w-full h-auto max-h-[520px] object-cover"
            />
          </motion.div>
        )}

        {/* Body */}
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-8 lg:col-start-3">
            <div className="prose-zine">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body}</ReactMarkdown>
            </div>

            {/* Tags footer */}
            <div className="mt-14 pt-6 border-t border-ink/25 flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap gap-2">
                {(post.tags || []).map((tag) => (
                  <span
                    key={tag}
                    className="border border-ink px-3 py-1.5 font-mono uppercase text-xs tracking-widest text-ink"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <Link
                to={`/${activeLang}/blog`}
                className="font-mono uppercase text-xs tracking-widest text-ink link-underline"
              >
                {t('blog.backToList')} ↗
              </Link>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};
